import { t } from 'i18next';

import { send } from 'loot-core/platform/client/connection';
import type {
  AccountEntity,
  SyncServerGoCardlessAccount,
} from 'loot-core/types/models';
import type { EnableBankingToken } from 'loot-core/types/models/enablebanking';

import { closeModal, pushModal } from '@desktop-client/modals/modalsSlice';
import { addNotification } from '@desktop-client/notifications/notificationsSlice';
import type { AppDispatch } from '@desktop-client/redux/store';

export async function deconfigureEnableBanking() {
  await send('enablebanking-configure', { applicationId: null, secret: null });
}

export async function selectEnableBankingAccounts(
  dispatch: AppDispatch,
  token: EnableBankingToken,
  accountEntity?: AccountEntity,
) {
  // converting accounts to "GoCardlessAccounts"
  const accounts: SyncServerGoCardlessAccount[] = token.accounts.map(
    enableBankingAccount => {
      return {
        ...enableBankingAccount,
        institution: { name: enableBankingAccount.institution },
        mask: '',
        official_name: enableBankingAccount.name,
      };
    },
  );
  if (accountEntity && accountEntity.account_id) {
    const account = accounts.find(
      tokenAccount =>
        tokenAccount.account_id &&
        tokenAccount.account_id === accountEntity.account_id,
    );
    if (account) {
      try {
        await send('enablebanking-accounts-link', {
          account,
          requisitionId: token.bank_id,
          upgradingId: accountEntity.id,
        });
        dispatch(
          addNotification({
            notification: {
              type: 'message',
              message: t(
                'Reauthorized bank sync via Enable Banking for {{accountName}}',
                {
                  accountName: accountEntity.name,
                },
              ),
            },
          }),
        );
        dispatch(closeModal());
        return;
      } catch (error) {
        console.error('Error linking account:', error);
        dispatch(
          addNotification({
            notification: {
              type: 'error',
              message: t(
                'There was an error linking the account. Please try again.',
              ),
            },
          }),
        );
      }
      return;
    }
  }

  dispatch(closeModal());
  dispatch(
    pushModal({
      modal: {
        name: 'select-linked-accounts',
        options: {
          requisitionId: token.bank_id,
          externalAccounts: accounts,
          syncSource: 'enablebanking',
        },
      },
    }),
  );
}

export async function authorizeEnableBankingSession(
  dispatch: AppDispatch,
  account?: AccountEntity,
  onUnlink?: () => void,
) {
  // For reauth, fetch the bank record to get the ASPSP identifier
  // The bank_id field contains "COUNTRY_BANKNAME" (e.g., "IT_ING" or "NL_ABN_AMRO")
  let initialCountry: string | undefined;
  let initialAspsp: string | undefined;

  if (account?.bank && account.account_sync_source === 'enablebanking') {
    try {
      // Fetch the bank record from the database
      const bankRecord = await send('get-bank', { id: account.bank });
      if (bankRecord?.bank_id) {
        const underscoreIndex = bankRecord.bank_id.indexOf('_');
        if (underscoreIndex > 0) {
          initialCountry = bankRecord.bank_id.substring(0, underscoreIndex);
          initialAspsp = bankRecord.bank_id.substring(underscoreIndex + 1);
        }
      }
    } catch (error) {
      console.error('Failed to fetch bank record for reauth:', error);
      // Continue with undefined initialCountry and initialAspsp
    }
  }

  dispatch(
    pushModal({
      modal: {
        name: 'enablebanking-setup-account',
        options: {
          onSuccess: async (token: EnableBankingToken) => {
            if (onUnlink) {
              onUnlink();
            }
            await selectEnableBankingAccounts(dispatch, token, account);
          },
          // Pre-select country and bank for re-authorization
          initialCountry,
          initialAspsp,
        },
      },
    }),
  );
}
